import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AdjustmentSuggestionView,
  CelebrationCue,
  HabitWeekReport,
  ObjectiveCheckpointAnswer,
  ObjectiveDateSuggestionView,
  ObjectiveId,
  RewardConfig,
  WeekStart,
  WeeklyReviewView,
} from '@contracts';
import { DEFAULT_REWARD_CONFIG } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { resolveLevelIdentity } from '@ui/shared/rewards/levelProgress';
import { XPToast } from '@ui/shared/celebrations/XPToast';
import { LevelUpMoment } from '@ui/shared/celebrations/LevelUpMoment';
import { BadgeEarnSheet } from '@ui/shared/celebrations/BadgeEarnSheet';
import { useCelebrationQueue } from '@ui/shared/celebrations/useCelebrationQueue';
import { formatWeekRange } from '@ui/shared/format/date';
import { FinishBar } from '@ui/desktop/weekly-review/FinishBar';
import { VerdictPanel } from './VerdictPanel';
import { HabitReviewCard } from './HabitReviewCard';
import { SuggestionPanel } from './SuggestionPanel';
import { ObjectivesPanel } from './ObjectivesPanel';

/**
 * `WeeklyReviewScreenPhone` (desktop-weekly-review.md v3.1 §9). Fixes the
 * reported bug: the desktop tree (six stacked panels incl. a 6-column habits
 * table with an inline sparkline) rendered unadapted at ~390px via the phone
 * Today view's `Week` footer link. Content, copy, XP values, and celebration
 * behavior are entirely unchanged from the desktop tree (§9's own framing)
 * — only layout differs. Data/domain logic here is intentionally IDENTICAL
 * to `WeeklyReviewScreenDesktop`; only the render tree forks.
 */
export function WeeklyReviewScreenPhone({
  onBack,
  onEditObjective,
}: {
  readonly onBack: () => void;
  readonly onEditObjective: (objectiveId: ObjectiveId) => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();

  const [weekStart, setWeekStart] = useState<WeekStart | null>(null);
  const [review, setReview] = useState<WeeklyReviewView | null>(null);
  const [rewardConfig, setRewardConfig] = useState<RewardConfig>(DEFAULT_REWARD_CONFIG);
  const [loadError, setLoadError] = useState(false);
  const [expandedHabitId, setExpandedHabitId] = useState<string | null>(null);

  const [declined, setDeclined] = useState<ReadonlySet<string>>(new Set());

  const celebration = useCelebrationQueue({
    resolveIdentity: (level) =>
      resolveLevelIdentity(
        { xpTotal: 0, level, xpIntoCurrentLevel: 0, xpRequiredForNextLevel: 100, streakDays: 0 },
        rewardConfig,
      ).identity,
  });

  const handleCue = useCallback(
    (cue: CelebrationCue, sourceLabel: string) => {
      if (cue.xpAwarded <= 0 && !cue.levelUp && cue.badgesEarned.length === 0) return;
      void api.getRewardSnapshot().then((snapshot) => {
        celebration.enqueue(cue, sourceLabel, snapshot.xpTotal);
      });
    },
    [api, celebration],
  );

  const load = useCallback(async () => {
    try {
      const [currentWeek, config] = await Promise.all([api.getCurrentReviewWeek(), api.getRewardConfig()]);
      const view = await api.getWeeklyReview(currentWeek);
      setWeekStart(currentWeek);
      setReview(view);
      setRewardConfig(config);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  useEffect(() => {
    setDeclined(new Set());
  }, [weekStart]);

  const declineSuggestion = useCallback((habitId: string, reviewWeek: WeekStart) => {
    setDeclined((prev) => new Set(prev).add(`${habitId}|${reviewWeek}`));
  }, []);

  const visibleHabits: readonly HabitWeekReport[] = useMemo(() => {
    if (!review) return [];
    return review.habits.map((h) =>
      h.suggestion && declined.has(`${h.habitId}|${h.suggestion.reviewWeek}`)
        ? { ...h, suggestion: null }
        : h,
    );
  }, [review, declined]);

  const suggestingHabits = useMemo(
    () => visibleHabits.filter((h): h is HabitWeekReport & { suggestion: AdjustmentSuggestionView } => h.suggestion !== null),
    [visibleHabits],
  );

  const untouchedObjectives = useMemo(
    () => (review ? review.objectives.filter((o) => !o.touchedThisWeek && o.checkpointAnswer === null) : []),
    [review],
  );

  const pendingCount = suggestingHabits.length + untouchedObjectives.length;

  const acceptHabitSuggestion = useCallback(
    async (suggestion: AdjustmentSuggestionView) => {
      const cue = await api.acceptSuggestion(suggestion);
      handleCue(cue, 'Smart adjustment');
      await load();
    },
    [api, handleCue, load],
  );

  const acceptObjectiveDate = useCallback(
    async (suggestion: ObjectiveDateSuggestionView) => {
      const cue = await api.acceptObjectiveDateSuggestion(suggestion);
      handleCue(cue, 'Date adjusted');
      await load();
    },
    [api, handleCue, load],
  );

  const answerCheckpoint = useCallback(
    async (objectiveId: ObjectiveId, answer: ObjectiveCheckpointAnswer) => {
      if (!weekStart) return;
      await api.answerObjectiveCheckpoint(objectiveId, weekStart, answer);
      await load();
    },
    [api, weekStart, load],
  );

  const finishReview = useCallback(async () => {
    if (!weekStart) return;
    const cue = await api.completeReview(weekStart);
    const bonusUnit = rewardConfig.xp.weeklyTargetBonus;
    const reviewXp = rewardConfig.xp.reviewCompleted;
    const targetsMet =
      bonusUnit > 0 ? Math.max(0, Math.round((cue.xpAwarded - reviewXp) / bonusUnit)) : 0;
    const sourceLabel =
      targetsMet > 0
        ? `Review + ${targetsMet} target${targetsMet === 1 ? '' : 's'} met`
        : 'Weekly review';
    handleCue(cue, sourceLabel);
    await load();
  }, [api, weekStart, rewardConfig, handleCue, load]);

  if (loadError) {
    return (
      <main aria-label="Weekly review" style={{ padding: 'var(--space-6) var(--layout-page-margin-phone)' }}>
        <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          Couldn't load the week. Your data is safe — try again.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          style={{
            marginTop: 'var(--space-4)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-5)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-strong)',
            color: 'var(--text-primary)',
            fontWeight: 600,
            fontSize: 'var(--type-body-size)',
          }}
        >
          Retry
        </button>
      </main>
    );
  }

  if (!review || !weekStart) {
    return (
      <main aria-label="Weekly review" style={{ padding: 'var(--space-6) var(--layout-page-margin-phone)' }}>
        <SkeletonBlock height={28} width={200} />
        <div style={{ height: 'var(--space-5)' }} />
        <SkeletonBlock height={140} />
        <div style={{ height: 'var(--layout-stack-gap)' }} />
        <SkeletonBlock height={100} />
        <div style={{ height: 'var(--layout-stack-gap)' }} />
        <SkeletonBlock height={100} />
      </main>
    );
  }

  const isEmpty = review.habits.length === 0 && review.objectives.length === 0;

  return (
    <main
      aria-label="Weekly review"
      style={{
        padding: 'var(--space-6) var(--layout-page-margin-phone) var(--space-16)',
      }}
    >
      <ReviewHeaderPhone
        weekStart={weekStart}
        available={review.available}
        completed={review.completed}
        reviewCompletedXp={rewardConfig.xp.reviewCompleted}
      />

      {isEmpty ? (
        <EmptyReview onBack={onBack} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--layout-stack-gap)', marginTop: 'var(--space-5)' }}>
          {!review.available && (
            <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
              The review for this week opens Sunday.
            </p>
          )}

          <VerdictPanel review={review} habits={visibleHabits} completed={review.completed} />

          {review.habits.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {visibleHabits.map((habit) => (
                <HabitReviewCard
                  key={habit.habitId}
                  habit={habit}
                  expanded={expandedHabitId === habit.habitId}
                  onToggle={() => setExpandedHabitId((prev) => (prev === habit.habitId ? null : habit.habitId))}
                />
              ))}
            </div>
          )}

          {review.available && suggestingHabits.length > 0 && (
            <SuggestionPanel
              suggestions={suggestingHabits}
              adjustmentAcceptedXp={rewardConfig.xp.adjustmentAccepted}
              onAccept={acceptHabitSuggestion}
              onDecline={(habitId, reviewWeek) => declineSuggestion(habitId, reviewWeek)}
            />
          )}

          {review.available && review.habits.length > 0 && suggestingHabits.length === 0 && !hasAnyTrendHistory(review) && (
            <p style={{ margin: 0, fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
              Adjustment suggestions appear once there's two weeks of history.
            </p>
          )}

          {review.objectives.length > 0 && (
            <ObjectivesPanel
              objectives={review.objectives}
              dateAdjustmentAcceptedXp={rewardConfig.xp.dateAdjustmentAccepted}
              onAcceptDateSuggestion={acceptObjectiveDate}
              onAnswerCheckpoint={answerCheckpoint}
              onEditObjective={onEditObjective}
            />
          )}

          {review.available && (
            <FinishBar
              completed={review.completed}
              pendingCount={pendingCount}
              reviewCompletedXp={rewardConfig.xp.reviewCompleted}
              onFinish={finishReview}
            />
          )}
        </div>
      )}

      {celebration.xpToast && (
        <XPToast amount={celebration.xpToast.amount} sourceLabel={celebration.xpToast.sourceLabel} onDismiss={celebration.dismissToast} />
      )}
      {celebration.levelUp && (
        <LevelUpMoment
          level={celebration.levelUp.level}
          identity={celebration.levelUp.identity}
          xpTotal={celebration.levelUp.xpTotal}
          onDismiss={celebration.dismissLevelUp}
        />
      )}
      {celebration.visibleBadgeId && (
        <ReviewBadgeEarnLoader badgeId={celebration.visibleBadgeId} onDismiss={celebration.dismissNextBadge} />
      )}
    </main>
  );
}

function hasAnyTrendHistory(review: WeeklyReviewView): boolean {
  return review.habits.some((h) => h.trend.length > 0);
}

function ReviewHeaderPhone({
  weekStart,
  available,
  completed,
  reviewCompletedXp,
}: {
  readonly weekStart: WeekStart;
  readonly available: boolean;
  readonly completed: boolean;
  readonly reviewCompletedXp: number;
}) {
  return (
    // §9.1: back-link stays App.tsx's wrapper (own phone-margin treatment,
    // see the switch's caller); this header stacks H1, week range, and the
    // status row on separate lines rather than desktop's single wide row —
    // `type.display` + the week-range string doesn't fit one line at 358px
    // usable width without an awkward wrap.
    <header>
      <h1
        style={{
          margin: 0,
          fontSize: 'var(--type-heading-size)',
          fontWeight: 'var(--type-heading-weight)',
          color: 'var(--text-primary)',
        }}
      >
        Weekly review
      </h1>
      <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
        {formatWeekRange(weekStart)}
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        <span
          style={{
            padding: '0 var(--space-3)',
            minHeight: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 'var(--radius-pill)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            background: completed ? 'color-mix(in srgb, var(--hue-primary-vivid) 14%, transparent)' : 'var(--surface-hover)',
            color: completed ? 'var(--hue-primary)' : 'var(--text-secondary)',
          }}
        >
          {completed ? 'Done' : 'In progress'}
        </span>
        {!completed && available && (
          <span
            className="num"
            style={{ fontSize: 'var(--type-label-size)', letterSpacing: 'var(--type-label-tracking)', color: 'var(--text-secondary)' }}
          >
            +{reviewCompletedXp} XP when you finish
          </span>
        )}
      </div>
    </header>
  );
}

function EmptyReview({ onBack }: { readonly onBack: () => void }) {
  return (
    <div style={{ marginTop: 'var(--space-8)', textAlign: 'center' }}>
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
        Nothing to review yet — set up a habit or an objective first.
      </p>
      <button
        type="button"
        onClick={onBack}
        style={{
          marginTop: 'var(--space-4)',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-5)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--hue-primary-vivid)',
          color: 'var(--text-on-accent)',
          fontWeight: 600,
          fontSize: 'var(--type-body-size)',
          border: '1px solid var(--hue-primary-strong)',
        }}
      >
        Go to Today
      </button>
    </div>
  );
}

function ReviewBadgeEarnLoader({ badgeId, onDismiss }: { readonly badgeId: string; readonly onDismiss: () => void }) {
  const api = useDomainApi();
  const [badge, setBadge] = useState<Awaited<ReturnType<typeof api.listBadges>>[number] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.listBadges().then((badges) => {
      if (cancelled) return;
      setBadge(badges.find((b) => b.badgeId === badgeId) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [api, badgeId]);

  if (!badge) return null;
  return <BadgeEarnSheet badge={badge} onDismiss={onDismiss} />;
}
